import React from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { useVideosData } from "@/hooks/useVideosData";
import { Lightbulb, Sparkles, LayoutGrid, ArrowRight } from "lucide-react";

export const VideosCategoriesPage: React.FC = () => {
  const { categories, loading, error, refetch } = useVideosData();

  const categoryIcons: Record<string, any> = {
    tips: Lightbulb,
    "before-after": Sparkles,
    setups: LayoutGrid,
  };

  return (
    <VideosLayout loading={loading} error={error} onRetry={refetch}>
      <SEOHead
        title="Video Categories | JNN Homes Northern Virginia"
        description="Browse JNN Homes vacation rental management video categories: hosting tips, before & after makeovers, and home setups for guests."
        canonical="https://www.jnnhomes.com/videos/categories"
      />

      <div className="space-y-8">
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <span className="text-slate-900 font-semibold">Categories</span>
        </nav>

        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">
            Video Categories
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Explore our vacation rental management videos by category.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {categories.map((cat) => {
            const Icon = categoryIcons[cat.slug] || LayoutGrid;
            return (
              <Link
                key={cat.slug}
                to={`/videos/c/${cat.slug}`}
                className="p-6 rounded-2xl border border-slate-200 bg-white hover:border-[#E8850F] hover:bg-slate-50/50 transition-all group space-y-3 shadow-xs"
              >
                <div className="p-3 rounded-xl bg-[#E8850F]/14 text-[#9A5207] inline-block">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 group-hover:text-[#9A5207] transition-colors">
                    {cat.name}
                  </h2>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                    {cat.description}
                  </p>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <span className="text-xs font-semibold text-slate-500">
                    {cat.comingSoon ? "New videos soon" : `${cat.count} videos`}
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#9A5207] group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </VideosLayout>
  );
};
